import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getProducts } from "../../utils/storage";

export default function BestSellers() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    const all = getProducts();
    const filtered = all
      .filter((p) => p.badge === "Best Seller" && p.isActive !== false)
      .slice(0, 12);
    setProducts(filtered);
  }, []);

  useEffect(() => {
    checkScroll();
  }, [products]);

  const checkScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const cardWidth = el.querySelector(".bs-card")?.offsetWidth || 280;
    el.scrollBy({ left: dir === "right" ? cardWidth + 18 : -(cardWidth + 18), behavior: "smooth" });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

        .bs-section {
          padding: clamp(48px, 8vw, 88px) 0 clamp(56px, 8vw, 96px);
          background: linear-gradient(160deg, #0d1f0e 0%, #111f12 55%, #0a180b 100%);
          position: relative; overflow: hidden;
          font-family: 'DM Sans', sans-serif;
        }
        .bs-section::before {
          content: ''; position: absolute; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          opacity: 0.40; pointer-events: none; z-index: 0;
        }
        .bs-section::after {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(132,204,22,0.18), transparent);
        }
        .bs-blob { position: absolute; border-radius: 50%; filter: blur(120px); pointer-events: none; }
        .bs-blob-1 { width: clamp(300px,42vw,540px); height: clamp(300px,42vw,540px); top: -160px; right: -120px; background: rgba(98,129,65,0.12); }
        .bs-blob-2 { width: clamp(180px,26vw,340px); height: clamp(180px,26vw,340px); bottom: -80px; left: -60px; background: rgba(132,204,22,0.06); }
        .bs-grid-bg {
          position: absolute; inset: 0;
          background-image: linear-gradient(rgba(132,204,22,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(132,204,22,0.035) 1px, transparent 1px);
          background-size: 52px 52px; pointer-events: none; z-index: 0;
        }
        .bs-inner { max-width: 85%; margin: 0 auto; padding: 0 clamp(16px,3vw,40px); position: relative; z-index: 1; }

        /* ── Header ── */
        .bs-header {
          display: flex; align-items: flex-end;
          justify-content: space-between;
          margin-bottom: clamp(20px, 3vw, 32px);
          gap: clamp(12px, 2vw, 24px);
        }
        .bs-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #84cc16; margin-bottom: 10px; opacity: 0.9; }
        .bs-eyebrow-line { width: 28px; height: 2px; background: linear-gradient(90deg, #84cc16, #a3e635); border-radius: 2px; }
        .bs-heading { font-family: 'Barlow Condensed', sans-serif; font-size: clamp(1.6rem,4vw,2.7rem); font-weight: 800; color: #f0f0f0; line-height: 1.08; letter-spacing: -.02em; margin: 0 0 10px; }
        .bs-heading span { background: linear-gradient(135deg, #84cc16, #a3e635); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .bs-subtitle { font-size: clamp(13px,1.5vw,15px); color: rgba(255,255,255,0.38); line-height: 1.6; max-width: 420px; margin: 0; }

        /* ── View All button ── */
        .bs-view-all {
          display: inline-flex; align-items: center; gap: 7px;
          font-size: clamp(12px,1.3vw,13.5px); font-weight: 600;
          color: rgba(163,230,53,0.85);
          border: 1.5px solid rgba(132,204,22,0.22);
          border-radius: 100px;
          padding: clamp(8px,1.2vw,10px) clamp(14px,2vw,22px);
          white-space: nowrap; cursor: pointer;
          background: rgba(132,204,22,0.06);
          font-family: 'DM Sans', sans-serif;
          flex-shrink: 0; align-self: flex-start; margin-top: 8px;
          transition: all .22s cubic-bezier(.4,0,.2,1);
          text-decoration: none; min-height: 44px;
        }
        .bs-view-all:hover { border-color: rgba(132,204,22,0.50); color: #a3e635; background: rgba(132,204,22,0.12); transform: translateX(3px); }
        .bs-view-all i { font-size: 15px; transition: transform .22s; }
        .bs-view-all:hover i { transform: translateX(4px); }

        /* ── Slider wrapper with side arrows ── */
        /* gap must exceed the track's -12px bleed margin; 32 - 12 = 20px visible */
        .bs-slider-outer {
          display: flex; align-items: center; gap: 32px;
        }
        .bs-arrow {
          flex-shrink: 0;
          width: 44px; height: 44px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          background: rgba(132,204,22,0.08);
          border: 1.5px solid rgba(132,204,22,0.22);
          color: #84cc16; font-size: 20px;
          cursor: pointer;
          transition: background .2s, border-color .2s, transform .2s, opacity .2s;
          z-index: 2;
        }
        .bs-arrow:hover:not(.bs-arrow--disabled) { background: rgba(132,204,22,0.18); border-color: rgba(132,204,22,0.55); transform: scale(1.1); }
        .bs-arrow--disabled { opacity: 0.22; cursor: default; pointer-events: none; }

        .bs-slider-wrap { position: relative; flex: 1; min-width: 0; }
        /* -12px matches the track's negative bleed margin, so the fade covers the
           real edge of the scrolling content instead of sitting 12px inside it. */
        .bs-fade-left { position: absolute; left: -12px; top: 0; bottom: 0; width: 48px; z-index: 2; background: linear-gradient(90deg, #0d1f0e 0%, transparent 100%); pointer-events: none; transition: opacity .25s; }
        .bs-fade-right { position: absolute; right: -12px; top: 0; bottom: 0; width: 48px; z-index: 2; background: linear-gradient(270deg, #0a180b 0%, transparent 100%); pointer-events: none; transition: opacity .25s; }

        /* overflow-x:auto makes the block axis clip too, so the card's hover ring
           gets cut off at every edge. Pad the track and cancel it with a negative
           margin: same layout, but room for the ring to render. */
        .bs-track {
          display: flex; gap: clamp(12px,1.8vw,18px);
          overflow-x: auto; scroll-behavior: smooth;
          padding: 12px; margin: -12px;
          padding-bottom: 20px; margin-bottom: -20px;
          -webkit-overflow-scrolling: touch; scrollbar-width: none;
        }
        .bs-track::-webkit-scrollbar { display: none; }

        /* ── Card ── */
        .bs-card { flex: 0 0 clamp(200px,22vw,280px); height: clamp(280px,32vw,360px); border-radius: 22px; overflow: hidden; position: relative; cursor: pointer; border: 1px solid rgba(132,204,22,0.10); box-shadow: 0 4px 24px rgba(0,0,0,0.40); transition: transform .32s cubic-bezier(.4,0,.2,1), box-shadow .32s cubic-bezier(.4,0,.2,1), border-color .22s; background: #131f14; }
        .bs-card:hover { transform: translateY(-8px) scale(1.015); box-shadow: 0 0 0 1.5px rgba(132,204,22,0.45); border-color: rgba(132,204,22,0.45); }
        .bs-card-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; padding: 16px; transition: transform .55s cubic-bezier(.4,0,.2,1); will-change: transform; }
        .bs-card:hover .bs-card-img { transform: scale(0.87); }
        .bs-card-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(5,12,5,0.96) 0%, rgba(5,12,5,0.52) 28%, rgba(5,12,5,0.08) 75%); transition: background .32s; z-index: 1; }
        .bs-card:hover .bs-card-overlay { background: linear-gradient(to top, rgba(5,12,5,0.98) 0%, rgba(5,12,5,0.65) 35%, rgba(5,12,5,0.15) 85%); }
        .bs-card-badge { position: absolute; top: 16px; left: 16px; z-index: 3; font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #0a1a00; padding: 4px 11px; border-radius: 100px; background: linear-gradient(135deg, #84cc16, #a3e635); border: 1px solid rgba(255,255,255,0.15); }
        .bs-card-icon { position: absolute; top: 16px; right: 16px; z-index: 3; width: 36px; height: 36px; background: rgba(132,204,22,0.10); backdrop-filter: blur(10px); border: 1px solid rgba(132,204,22,0.22); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #84cc16; font-size: 17px; transition: background .22s, transform .22s cubic-bezier(.4,0,.2,1); }
        .bs-card:hover .bs-card-icon { background: rgba(132,204,22,0.22); transform: scale(1.12) rotate(-6deg); }
        .bs-card-content { position: absolute; bottom: 0; left: 0; right: 0; z-index: 2; padding: clamp(16px,2.5vw,24px) clamp(14px,2vw,20px) clamp(14px,2vw,20px); }
        .bs-card-brand { font-size: 11px; font-weight: 900; letter-spacing: .1em; text-transform: uppercase; color: rgba(132,204,22,0.60); margin-bottom: 5px; }
        .bs-card-title { font-family: 'Barlow Condensed', sans-serif; font-size: clamp(17px,2.2vw,21px); font-weight: 800; color: #f0f0f0; line-height: 1.1; margin-bottom: 6px; letter-spacing: -.01em; }
        .bs-card-desc { font-size: clamp(11.5px,1.2vw,12.5px); color: rgba(255,255,255,0.42); line-height: 1.5; margin-bottom: 12px; font-family: 'DM Sans', sans-serif; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .bs-card-cta { display: inline-flex; align-items: center; gap: 6px; font-family: 'DM Sans', sans-serif; font-size: 12.5px; font-weight: 700; color: #84cc16; letter-spacing: .04em; text-transform: uppercase; transition: gap .22s cubic-bezier(.4,0,.2,1); }
        .bs-card:hover .bs-card-cta { gap: 10px; }
        .bs-card-cta i { font-size: 15px; }
        .bs-card-no-img { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 56px; background: #131f14; }

        @media (max-width: 1400px) { .bs-inner { max-width: 95%; } }
        @media (max-width: 1200px) { .bs-inner { max-width: 100%; } .bs-card { height: clamp(260px,28vw,320px); } }
        @media (max-width: 992px) { .bs-header { flex-direction: column; align-items: flex-start; gap: 16px; } .bs-card { height: clamp(240px,30vw,300px); border-radius: 18px; } }
        @media (max-width: 768px) {
          .bs-inner { padding: 0 16px; }
          .bs-arrow { display: none; }
          .bs-card { flex: 0 0 clamp(180px,42vw,240px); height: clamp(220px,48vw,280px); border-radius: 16px; }
          .bs-card-icon { width: 32px; height: 32px; font-size: 15px; border-radius: 8px; }
          .bs-card-badge { font-size: 9.5px; padding: 3px 9px; }
          .bs-fade-left, .bs-fade-right { width: 28px; }
        }
        @media (max-width: 520px) {
          .bs-card { flex: 0 0 clamp(160px,60vw,210px); height: clamp(200px,52vw,250px); border-radius: 14px; }
          .bs-card-desc { display: none; }
          .bs-card-content { padding: 14px 12px 12px; }
          .bs-subtitle { display: none; }
          .bs-view-all span { display: none; }
        }
      `}</style>

      <section className="bs-section">
        <div className="bs-blob bs-blob-1" />
        <div className="bs-blob bs-blob-2" />
        <div className="bs-grid-bg" />

        <div className="bs-inner">
          <div className="bs-header">
            <div>
              <div className="bs-eyebrow"><div className="bs-eyebrow-line" />Customer Favourites</div>
              <h2 className="bs-heading">Best <span>Sellers</span></h2>
              <p className="bs-subtitle">The most trusted products among HVACR professionals — proven on the field.</p>
            </div>
            <button className="bs-view-all" onClick={() => navigate("/best-sellers")}>
              View All Products <i className="ri-arrow-right-line" />
            </button>
          </div>

          {products.length === 0 && (
            <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "14px", textAlign: "center", padding: "40px 0" }}>
              No best sellers yet. Tag a product as "Best Seller" from the admin panel.
            </p>
          )}

          <div className="bs-slider-outer">
            <button
              className={`bs-arrow${!canScrollLeft ? " bs-arrow--disabled" : ""}`}
              onClick={() => scroll("left")}
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            <div className="bs-slider-wrap">
              <div className="bs-fade-left" style={{ opacity: canScrollLeft ? 1 : 0 }} />
              <div className="bs-fade-right" style={{ opacity: canScrollRight ? 1 : 0 }} />
              <div className="bs-track" ref={trackRef} onScroll={checkScroll}>
                {products.map((product) => (
                  <div
                    className="bs-card"
                    key={product.id}
                    onClick={() => navigate(`/category/${product.categorySlug}/${product.id}`)}
                    role="button" tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/category/${product.categorySlug}/${product.id}`)}
                  >
                    {product.images?.[0] ? (
                      <img className="bs-card-img" src={product.images[0]} alt={product.name} />
                    ) : (
                      <div className="bs-card-no-img">📦</div>
                    )}
                    <div className="bs-card-overlay" />
                    <div className="bs-card-badge">Best Seller</div>
                    <div className="bs-card-icon"><i className="ri-star-line" /></div>
                    <div className="bs-card-content">
                      <div className="bs-card-brand">{product.brand}</div>
                      <div className="bs-card-title">{product.name}</div>
                      <div className="bs-card-desc">{product.shortDescription}</div>
                      <div className="bs-card-cta">View Product <i className="ri-arrow-right-line" /></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              className={`bs-arrow${!canScrollRight ? " bs-arrow--disabled" : ""}`}
              onClick={() => scroll("right")}
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}